import { Component } from '@angular/core';

import {
    DOC_TYPES, DocType, FoundDocument, INPUT_TYPES, InputDocType, Landscape, TYPE_ORDER
} from '~/models/document-finder.models';
import { DocumentFinderService } from '~/services/document-finder.service';
import { PackExportService } from '~/services/pack-export.service';
import { downloadBlob } from '~/utils/download';

@Component({
    selector: 'df-publish-step',
    templateUrl: './df-publish-step.component.html',
    styleUrls: ['./df-publish-step.component.scss'],
    standalone: false
})
export class DfPublishStepComponent {
    public readonly inputTypes = INPUT_TYPES;
    public readonly landscapes: Landscape[] = ['asis', 'tobe'];
    public readonly docTypes = DOC_TYPES as Record<string, (typeof DOC_TYPES)[keyof typeof DOC_TYPES]>;

    public readonly today = new Date();

    /** Signed-in user's display name (falls back to the name reported by the API). */
    public get reviewer(): string {
        try {
            const user = JSON.parse(sessionStorage.getItem('_AppSettings') ?? '{}')?.user;
            return user?.name || user?.username || this.df.user || '';
        } catch {
            return this.df.user || '';
        }
    }

    constructor(public df: DocumentFinderService, public pack: PackExportService) {}

    public get packName(): string { return this.pack.packName; }
    public set packName(v: string) { this.pack.packName = v; }
    public get packNote(): string { return this.pack.packNote; }
    public set packNote(v: string) { this.pack.packNote = v; }

    public get included(): FoundDocument[] { return this.df.included(); }

    public get groups(): { type: DocType; docs: FoundDocument[] }[] {
        const inc = this.included;
        return TYPE_ORDER.filter(t => inc.some(d => d.t === t)).map(t => ({ type: t, docs: inc.filter(d => d.t === t) }));
    }

    public readonly trackByType = (_: number, g: { type: string }) => g.type;

    /** Count of included docs for a type/landscape, or null if the combination is not applicable. */
    public cellCount(t: InputDocType, land: Landscape): number | null {
        const count = this.included.filter(d => d.t === t && d.land === land).length;
        if (count) { return count; }
        if (!DOC_TYPES[t][land]) { return null; }
        return 0;
    }

    /** Publishes the pack and downloads all included files as a ZIP. */
    public publish(): Promise<void> {
        return this.pack.publishAndDownload();
    }

    /** Re-downloads the ZIP of an already published pack. */
    public downloadZip(): Promise<unknown> {
        return this.pack.download();
    }

    public exportCsv(): void {
        const blob = new Blob(['\uFEFF' + this.df.buildCsv()], { type: 'text/csv;charset=utf-8' });
        downloadBlob(blob, 'Explore_Inputs.csv');
        this.df.toast(`Exported ${this.included.length} documents to CSV`);
    }

    public copyLinks(): void {
        const text = this.df.buildLinks();
        if (!navigator.clipboard) {
            this.df.toast('Copy is blocked in this browser');
            return;
        }
        navigator.clipboard.writeText(text).then(
            () => this.df.toast('Links copied'),
            () => this.df.toast('Copy is blocked in this browser')
        );
    }

    public open(id: string): void {
        this.df.openDrawer(id);
    }

    public startNew(): void {
        this.df.startNew();
    }
}
